import { useEffect, useRef, useState } from "react";
import { Canvas, FabricImage, Circle, PencilBrush, Point } from "fabric";
import { IconButton, Stack, Box, Slider } from "@mui/material";
import {
  Gesture,
  AddLocation,
  DeleteSweep,
  RestartAlt,
  CleaningServices,
} from "@mui/icons-material";
import { getBase64FromUrl } from "utils/imageUtils";

const COLORS = [
  { name: "Red", value: "#d32f2f" },
  { name: "Blue", value: "#1976d2" },
  { name: "Green", value: "#2e7d32" },
  { name: "Black", value: "#000000" },
  { name: "Yellow", value: "#fbc02d" },
];
const BodyDiagramCanvas = ({
  bodyImageUrl,
  onSave,
  imageHight,
  useZoomedExport = false,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [activeColor, setActiveColor] = useState(COLORS[0].value);
  const [activeTool, setActiveTool] = useState("draw");
  const activeToolRef = useRef(activeTool);
  const [brushSize, setBrushSize] = useState(4);
  const [backgroundImageId, setBackgroundImageId] = useState(null);

  // Sync ref with state
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  const emitChanges = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const vpt = [...canvas.viewportTransform];
    const objects = canvas.getObjects();
    const bgImage = objects.find((obj) => obj.get("type") === "image");

    if (bgImage && !useZoomedExport) bgImage.set({ visible: false });

    let responsiveSvg;

    if (useZoomedExport) {
      const scaleX = vpt[0]; // zoom level
      const scaleY = vpt[3];
      const tx = vpt[4]; // pan X
      const ty = vpt[5]; // pan Y
      const logicalLeft = -tx / scaleX;
      const logicalTop = -ty / scaleY;
      const logicalWidth = canvas.width / scaleX;
      const logicalHeight = canvas.height / scaleY;

      // Reset viewport to export at native coordinates
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

      const svg = canvas.toSVG({
        suppressPreamble: true,
        includeDefaultValues: false,
        viewBox: {
          x: logicalLeft,
          y: logicalTop,
          width: logicalWidth,
          height: logicalHeight,
        },
      });

      responsiveSvg = svg
        .replace(/width=".*?"/, 'width="100%"')
        .replace(/height=".*?"/, 'height="100%"')
        .replace("<svg ", '<svg preserveAspectRatio="xMidYMid meet" ');

      canvas.setViewportTransform(vpt);
    } else {
      // Standard export: reset viewport and capture at full canvas size
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

      const svg = canvas.toSVG({
        suppressPreamble: true,
        includeDefaultValues: false,
        viewBox: { x: 0, y: 0, width: canvas.width, height: canvas.height },
      });

      responsiveSvg = svg
        .replace(/width=".*?"/, 'width="100%"')
        .replace(/height=".*?"/, 'height="100%"')
        .replace("<svg ", '<svg preserveAspectRatio="none" ');

      canvas.setViewportTransform(vpt);
    }

    if (bgImage) bgImage.set({ visible: true });
    canvas.renderAll();
    onSave(responsiveSvg);
  };

  useEffect(() => {
    const containerWidth = containerRef.current?.offsetWidth || 600;
    const canvas = new Canvas(canvasRef.current, {
      width: containerWidth,
      height: imageHight,
      selection: true,
      allowTouchScrolling: false,
    });
    fabricCanvasRef.current = canvas;

    // Enable drawing mode by default
    canvas.isDrawingMode = true;
    const brush = new PencilBrush(canvas);
    brush.width = brushSize;
    brush.color = activeColor;
    canvas.freeDrawingBrush = brush;

    let lastDist = 0;
    let lastMid = null;
    let isZooming = false;

    const getTouchData = (e) => {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const d = Math.hypot(t2.pageX - t1.pageX, t2.pageY - t1.pageY);
      const m = new Point((t1.pageX + t2.pageX) / 2, (t1.pageY + t2.pageY) / 2);
      return { d, m };
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        if (!isZooming) {
          isZooming = true;
          canvas.isDrawingMode = false;
        }
        const { d, m } = getTouchData(e);
        const zoom = canvas.getZoom();
        if (lastDist > 0) {
          const newZoom = zoom * (d / lastDist);
          const limitZoom = Math.min(Math.max(newZoom, 0.5), 10);
          const rect = canvas.getElement().getBoundingClientRect();
          const point = new Point(m.x - rect.left, m.y - rect.top);
          canvas.zoomToPoint(point, limitZoom);
        }
        if (lastMid) {
          const vpt = canvas.viewportTransform;
          vpt[4] += m.x - lastMid.x;
          vpt[5] += m.y - lastMid.y;
          canvas.requestRenderAll();
        }
        lastDist = d;
        lastMid = m;
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        lastDist = 0;
        lastMid = null;
        setTimeout(() => {
          isZooming = false;
          // Restore mode after zoom ends
          if (activeTool === "draw") {
            canvas.isDrawingMode = true;
          } else if (activeTool === "eraser") {
            canvas.isDrawingMode = false;
            canvas.defaultCursor = "crosshair";
          }
        }, 50);
      }
    };

    const canvasElem = canvas.getElement().parentElement;
    canvasElem.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    canvasElem.addEventListener("touchend", handleTouchEnd);

    canvas.on("mouse:wheel", (opt) => {
      const delta = opt.e.deltaY;
      let zoom = canvas.getZoom();
      zoom *= 0.999 ** delta;
      zoom = Math.min(Math.max(zoom, 0.5), 10);
      canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
      opt.e.preventDefault();
    });

    canvas.on("path:created", (opt) => {
      if (activeToolRef.current === "eraser") {
        opt.path.set({
          globalCompositeOperation: "destination-out",
          selectable: false,
          evented: false,
        });
        canvas.renderAll();
      }
      emitChanges();
    });
    canvas.on("object:modified", emitChanges);
    canvas.on("object:removed", emitChanges);
    getBase64FromUrl(bodyImageUrl).then((base64) => {
      FabricImage.fromURL(base64, { crossOrigin: "anonymous" }).then((img) => {
        img.set({
          selectable: false,
          evented: false,
          scaleX: canvas.width / img.width,
          scaleY: canvas.height / img.height,
          left: 0,
          top: 0,
        });
        canvas.add(img);
        canvas.sendObjectToBack(img);
        setBackgroundImageId(img.id);
        canvas.renderAll();
      });
    });

    return () => canvas.dispose();
  }, [bodyImageUrl, imageHight]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    if (activeTool === "draw" || activeTool === "eraser") {
      canvas.isDrawingMode = true;
      const brush = new PencilBrush(canvas);
      brush.width = activeTool === "eraser" ? brushSize * 2 : brushSize;
      brush.color = activeTool === "eraser" ? "#ffffff" : activeColor;
      canvas.freeDrawingBrush = brush;
    } else {
      canvas.isDrawingMode = false;
    }
  }, [activeColor, activeTool, brushSize]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        border: "2px solid #1976d2",
        borderRadius: "8px",
        overflow: "hidden",
        touchAction: "none",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          p: 2,
          bgcolor: "#ffffff",
          flexWrap: "wrap",
          borderBottom: "2px solid #e0e0e0",
          alignItems: "center",
        }}
      >
        {/* Color Picker Section */}
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#666" }}>
            Colors:
          </Box>
          <Stack direction="row" spacing={0.75}>
            {COLORS.map((c) => (
              <Box
                key={c.value}
                onClick={() => {
                  setActiveColor(c.value);
                  setActiveTool("draw");
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: c.value,
                  cursor: "pointer",
                  border:
                    activeColor === c.value && activeTool === "draw"
                      ? "3px solid #000"
                      : "2px solid #ddd",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
                title={c.name}
              />
            ))}
          </Stack>
        </Stack>

        {/* Tools Section */}
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#666" }}>
            Tools:
          </Box>
          <IconButton
            color={activeTool === "draw" ? "primary" : "default"}
            onClick={() => setActiveTool("draw")}
            sx={{
              backgroundColor:
                activeTool === "draw" ? "#e3f2fd" : "transparent",
              border:
                activeTool === "draw" ? "2px solid #1976d2" : "2px solid #ddd",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#e3f2fd",
              },
            }}
            title="Draw"
          >
            <Gesture />
          </IconButton>
          <IconButton
            color={activeTool === "eraser" ? "primary" : "default"}
            onClick={() => setActiveTool("eraser")}
            sx={{
              backgroundColor:
                activeTool === "eraser" ? "#ffe0b2" : "transparent",
              border:
                activeTool === "eraser"
                  ? "2px solid #ff9800"
                  : "2px solid #ddd",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#ffe0b2",
              },
            }}
            title="Eraser"
          >
            <CleaningServices />
          </IconButton>
        </Stack>

        {/* Brush Size Section */}
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#666" }}>
            Size:
          </Box>
          <Box sx={{ width: 100 }}>
            <Slider
              size="small"
              value={brushSize}
              min={1}
              max={20}
              onChange={(e, val) => setBrushSize(val)}
              sx={{
                color: "#1976d2",
                "& .MuiSlider-thumb": {
                  backgroundColor: "#fff",
                  border: "2px solid #1976d2",
                },
              }}
            />
          </Box>
          <Box sx={{ fontSize: "0.75rem", color: "#999", minWidth: "25px" }}>
            {brushSize}px
          </Box>
        </Stack>

        {/* Undo Button */}
        <Box sx={{ ml: "auto" }}>
          <IconButton
            onClick={() => {
              const canvas = fabricCanvasRef.current;
              if (!canvas) return;

              const objects = canvas.getObjects();
              // Find the last drawn object (skip background image)
              for (let i = objects.length - 1; i >= 0; i--) {
                const obj = objects[i];
                if (obj.get("type") !== "image") {
                  canvas.remove(obj);
                  canvas.renderAll();
                  emitChanges();
                  return;
                }
              }
            }}
            sx={{
              backgroundColor: "#ffffff",
              border: "2px solid #e0e0e0",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            title="Undo Last Step"
          >
            <RestartAlt sx={{ color: "#757575" }} />
          </IconButton>
        </Box>
      </Stack>

      {/* Canvas Area */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#ffffff",
          p: 2,
          minHeight: "400px",
          position: "relative",
        }}
      >
        <canvas ref={canvasRef} style={{ borderRadius: "4px" }} />
      </Box>
    </Box>
  );
};

export default BodyDiagramCanvas;
